import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { isFedrampModerate } from '@atlaskit/atlassian-context';
import { fg } from '@atlaskit/platform-feature-flags';

import GlobalInteractionSessionTracking from './globalInteractionSessionTracking';
import type InteractionSessionTracking from './interactionSessionTracking';
import SafeSessionStorage from './storage/SafeSessionStorage';

export const INTERACTION_SESSION_SOURCE_KEY = 'interactionSession.source';
export const INTERACTION_SESSION_BRIDGE_KEY = 'interactionSession.bridge';

export type InteractionSessionTrackingOptions = {
	analyticsEventCallback?: Function;
	persistedSessionId?: string;
};

/**
 * AnalyticsCrossProductInteractionClient is a wrapper for the InteractionSessionTracking class.
 * It is designed to be used internally within the Atlassian Analytics Pipeline as well as useCrossProductUrlWrapper hook.
 * It is not intended to be used directly by end-users.
 *
 * This will create an instance of the interaction session tracker on the window object, which can be shared across different parts of a document.
 * If the internal client already exists, it will use the existing instance. Creating the client in a singleton manner like this enables us
 * to debounce updates to Session Storage, which is important when a large number of links using our hook are rendered on a page.
 *
 * The other interaction session parameters are written directly to session storage as they are predominantly read-only,
 * with the only update being the initialisation of the client
 */
export default class AnalyticsCrossProductInteractionClient {
	private safeSessionStorage?: SafeSessionStorage;
	private interactionSessionTracking?: InteractionSessionTracking;
	private isEnabled: boolean;

	constructor(
		interactionSessionOptions?: InteractionSessionTrackingOptions,
		bridge?: string,
		interactionSource?: string,
	) {
		const shouldDisable = fg('use_frontend_feature_encoding_sdk_platform')
			? !isFeatureEnabled('atlassian-analytics', 'atlassian-analytics-cross-product-interaction-client')
			: isFedrampModerate();
		this.isEnabled = !shouldDisable;

		if (this.isEnabled) {
			// Get singleton interaction session tracking instance
			this.interactionSessionTracking = GlobalInteractionSessionTracking.getInstance();

			let isValidPersistedId = false;

			if (this.interactionSessionTracking) {
				if (interactionSessionOptions) {
					this.interactionSessionTracking.setAnalyticsCallback(
						interactionSessionOptions.analyticsEventCallback,
					);
					isValidPersistedId = this.interactionSessionTracking.setInteractionSessionId(
						interactionSessionOptions.persistedSessionId,
					);
				}
			}

			this.safeSessionStorage = new SafeSessionStorage();

			// Store other interaction session parameters directly in session storage if ID has been validated
			if (isValidPersistedId) {
				bridge && this.safeSessionStorage.setItem(INTERACTION_SESSION_BRIDGE_KEY, bridge);
				interactionSource &&
					this.safeSessionStorage.setItem(INTERACTION_SESSION_SOURCE_KEY, interactionSource);
			}
		}
	}

	public getCurrentInteractionSessionId = (): string | null =>
		this.isEnabled && this.interactionSessionTracking
			? this.interactionSessionTracking.getCurrentInteractionSessionId()
			: null;

	public getPreviousInteractionSessionId = (): string | null =>
		this.isEnabled && this.interactionSessionTracking
			? this.interactionSessionTracking.getPreviousInteractionSessionId()
			: null;

	public getBridge = (): string | null =>
		this.isEnabled && this.safeSessionStorage
			? this.safeSessionStorage.getItem(INTERACTION_SESSION_BRIDGE_KEY)
			: null;

	public getInteractionSource = (): string | null =>
		this.isEnabled && this.safeSessionStorage
			? this.safeSessionStorage.getItem(INTERACTION_SESSION_SOURCE_KEY)
			: null;
}
